/**
 * The key exchange behind the pairing QR.
 *
 * WebRTC already encrypts a data channel with DTLS, so this is not what stops a stranger on the
 * Wi-Fi reading the stream. It is what stops a stranger *becoming* the other end: the QR carries
 * the portal's public key, and only a phone that saw the QR can produce a message this browser
 * will accept. Without it, "a direct connection over your local network" is a connection to
 * whoever answered first.
 *
 * WebCrypto only, no dependency. P-256 ECDH to a shared secret, HKDF to an AES-GCM key, a random
 * 96-bit nonce per message. The private key is generated per pairing and is not extractable, so
 * it cannot be read back out of the tab even by this code, and it dies with the page.
 */

const KEY_ALGORITHM: EcKeyGenParams = { name: 'ECDH', namedCurve: 'P-256' }

export interface PairingKeys {
  privateKey: CryptoKey
  /** Raw uncompressed P-256 point, base64url, small enough to ride in a QR beside the offer. */
  publicKeyB64: string
}

function toBase64Url(bytes: ArrayBuffer): string {
  let binary = ''
  const view = new Uint8Array(bytes)
  for (let i = 0; i < view.length; i += 1) binary += String.fromCharCode(view[i] ?? 0)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
  const bytes = new Uint8Array(new ArrayBuffer(binary.length))
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

export async function generatePairingKeys(): Promise<PairingKeys> {
  const pair = await crypto.subtle.generateKey(KEY_ALGORITHM, false, ['deriveKey'])
  const raw = await crypto.subtle.exportKey('raw', pair.publicKey)
  return { privateKey: pair.privateKey, publicKeyB64: toBase64Url(raw) }
}

/**
 * The session key both ends arrive at without ever sending it.
 *
 * The salt is the pairing id, which is in the QR and therefore public. That is fine and is what
 * HKDF salts are for: it binds the derived key to this one pairing, so a key from an earlier
 * session cannot be replayed into a later one.
 */
export async function deriveSessionKey(
  privateKey: CryptoKey,
  peerPublicKeyB64: string,
  pairingId: string,
): Promise<CryptoKey> {
  const peerPublicKey = await crypto.subtle.importKey(
    'raw',
    fromBase64Url(peerPublicKeyB64),
    KEY_ALGORITHM,
    false,
    [],
  )
  const shared = await crypto.subtle.deriveKey(
    { name: 'ECDH', public: peerPublicKey },
    privateKey,
    { name: 'HKDF', hash: 'SHA-256' } as unknown as AesKeyAlgorithm,
    false,
    ['deriveKey'],
  ).catch(async () => {
    // Safari refuses deriveKey straight into HKDF, so fall back to deriving raw bits and
    // importing them. Same output, one more hop.
    const bits = await crypto.subtle.deriveBits(
      { name: 'ECDH', public: peerPublicKey },
      privateKey,
      256,
    )
    return crypto.subtle.importKey('raw', bits, 'HKDF', false, ['deriveKey'])
  })

  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode(pairingId),
      info: new TextEncoder().encode('covella-portal-link-v1'),
    },
    shared,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

/** Nonce prepended to the ciphertext, so one string carries everything decrypt needs. */
export async function seal(key: CryptoKey, plaintext: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(plaintext),
  )
  const joined = new Uint8Array(iv.length + encrypted.byteLength)
  joined.set(iv, 0)
  joined.set(new Uint8Array(encrypted), iv.length)
  return toBase64Url(joined.buffer)
}

export async function open(key: CryptoKey, sealed: string): Promise<string> {
  const bytes = fromBase64Url(sealed)
  const iv = bytes.slice(0, 12)
  const body = bytes.slice(12)
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, body)
  return new TextDecoder().decode(plain)
}

/**
 * A short human-readable fingerprint of the session key.
 *
 * Both screens show it and she checks they match. This is the part that makes the pairing
 * genuinely hers rather than merely encrypted: it is the one step an attacker who intercepted
 * the exchange cannot fake, and it costs her a two-second glance.
 */
export async function fingerprint(publicKeyB64: string, peerPublicKeyB64: string): Promise<string> {
  const ordered = [publicKeyB64, peerPublicKeyB64].sort().join('|')
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(ordered))
  const view = new Uint8Array(digest)
  // Six hex characters in two groups. Long enough that guessing it is not worth an attacker's
  // afternoon, short enough that she will actually read it.
  const hex = Array.from(view.slice(0, 3))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()
  return `${hex.slice(0, 3)} ${hex.slice(3)}`
}

export { toBase64Url, fromBase64Url }
